import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, MapPin, Clock, Users, ArrowLeft, Check, X, MessageCircle, Star } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { RouteMap } from "@/components/maps";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function RideDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/rides/${id}`).then(({ data }) => setRide(data)).catch(() => setRide(false)), [id]);
  useEffect(() => { load(); }, [load]);

  if (ride === null) return <p className="text-muted-foreground animate-pulse text-center py-10">Loading ride…</p>;
  if (ride === false) return <p className="text-center py-10">Ride not found.</p>;

  const requestSeat = async () => {
    setBusy(true);
    try {
      await api.post(`/rides/${id}/request`, { note, seats: 1 });
      toast.success("Seat requested! The driver will respond soon.");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const respond = async (reqId, action) => {
    try {
      await api.post(`/rides/requests/${reqId}/respond`, { action });
      toast.success(action === "accept" ? "Passenger confirmed!" : "Request declined");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rideAction = async (action) => {
    try {
      await api.post(`/rides/${id}/${action}`);
      toast.success(action === "cancel" ? "Ride cancelled" : "Ride completed 🎉");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openChat = async () => {
    try {
      const { data } = await api.post("/chat/open", { type: "ride", ref_id: id });
      navigate(`/chat?open=${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="ride-detail-page">
      <button data-testid="ride-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors duration-200">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="t-card p-6">
        <div className="flex items-center justify-between">
          <span className="t-chip bg-primary/10 text-primary">{ride.vehicle_type}</span>
          <span className="t-chip bg-black/5">{ride.status}</span>
        </div>
        <h1 className="font-display font-bold text-xl mt-4 flex items-center gap-2 flex-wrap">
          <MapPin className="w-5 h-5 text-primary" /> {ride.from_location} → {ride.to_location}
        </h1>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {ride.date} at {ride.departure_time}</span>
          <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {ride.seats_left}/{ride.available_seats} seats left</span>
          <span className="font-bold text-foreground">₹{ride.contribution} per seat</span>
        </div>
        {ride.description && <p className="mt-3 text-sm leading-relaxed">{ride.description}</p>}
        <div className="flex items-center gap-3 mt-5 pt-5 border-t border-black/5">
          <div className="w-10 h-10 rounded-full bg-secondary/30 flex items-center justify-center font-bold">{ride.driver.name[0]}</div>
          <div>
            <p className="font-bold text-sm flex items-center gap-1">
              {ride.driver.name}
              {ride.driver.verification_status === "verified" && <BadgeCheck className="w-4 h-4 text-primary" />}
            </p>
            <p className="text-xs text-muted-foreground">{ride.driver.completed_rides} completed rides{ride.driver.vehicle_model ? ` · ${ride.driver.vehicle_model}` : ""}</p>
            {ride.driver.phone && (
              <p className="text-xs font-bold text-primary" data-testid="driver-phone">📞 {ride.driver.phone} (shared after confirmation)</p>
            )}
          </div>
        </div>
      </div>

      <RouteMap from={{ lat: ride.from_lat, lng: ride.from_lng }} to={{ lat: ride.to_lat, lng: ride.to_lng }} />

      {(ride.is_mine || ["accepted", "completed"].includes(ride.my_request?.status)) && (
        <Button data-testid="open-ride-chat-btn" onClick={openChat} variant="outline" className="w-full rounded-full font-bold">
          <MessageCircle className="w-4 h-4 mr-2" /> Ride chat
        </Button>
      )}

      {ride.status === "completed" && ride.rateable?.length > 0 && (
        <div className="t-card p-5 space-y-3" data-testid="rating-section">
          <h2 className="font-display font-bold text-sm">Rate your ride</h2>
          {ride.rateable.map((p) => (
            <RateDialog key={p.id} rideId={id} person={p} onDone={load} />
          ))}
        </div>
      )}

      {ride.passengers?.length > 0 && (
        <div className="t-card p-5">
          <h2 className="font-display font-bold text-sm mb-3">Confirmed passengers</h2>
          <div className="flex flex-wrap gap-2">
            {ride.passengers.map((p) => (
              <span key={p.id} className="t-chip bg-secondary/20 text-[#14532D] normal-case tracking-normal">{p.name}</span>
            ))}
          </div>
        </div>
      )}

      {!ride.is_mine && ride.status === "active" && (
        ride.my_request ? (
          <div className="t-card p-5 text-center" data-testid="my-request-status">
            <p className="text-sm">Your request is <span className="font-bold">{ride.my_request.status}</span>.</p>
          </div>
        ) : (
          <div className="t-card p-5 space-y-3">
            <Textarea data-testid="request-note-input" placeholder="Note for the driver (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-2xl" />
            <Button data-testid="request-seat-btn" disabled={busy || ride.seats_left < 1} onClick={requestSeat} className="w-full rounded-full font-bold">
              {ride.seats_left < 1 ? "Ride is full" : busy ? "Requesting…" : "Request a seat"}
            </Button>
          </div>
        )
      )}

      {ride.is_mine && (
        <>
          <div className="t-card p-5">
            <h2 className="font-display font-bold text-sm mb-3">Seat requests</h2>
            {ride.requests?.length ? (
              <div className="space-y-3">
                {ride.requests.map((rq) => (
                  <div key={rq.id} className="flex items-center gap-3 border border-black/5 rounded-2xl p-3" data-testid={`seat-request-${rq.id}`}>
                    <div className="w-8 h-8 rounded-full bg-secondary/30 flex items-center justify-center text-xs font-bold">{rq.passenger?.name?.[0]}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold">{rq.passenger?.name} <span className="font-normal text-muted-foreground">· {rq.seats} seat(s)</span></p>
                      {rq.note && <p className="text-xs text-muted-foreground truncate">{rq.note}</p>}
                    </div>
                    {rq.status === "requested" ? (
                      <div className="flex gap-2">
                        <button data-testid={`accept-request-${rq.id}`} onClick={() => respond(rq.id, "accept")}
                          className="w-9 h-9 rounded-full bg-secondary/20 text-[#14532D] flex items-center justify-center hover:bg-secondary/40 transition-colors duration-200"><Check className="w-4 h-4" /></button>
                        <button data-testid={`reject-request-${rq.id}`} onClick={() => respond(rq.id, "reject")}
                          className="w-9 h-9 rounded-full bg-destructive/10 text-destructive flex items-center justify-center hover:bg-destructive/20 transition-colors duration-200"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <span className="t-chip bg-black/5">{rq.status}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="no-seat-requests">No requests yet. Share the ride with your buddies!</p>
            )}
          </div>
          {ride.status === "active" && (
            <div className="flex gap-3">
              <Button data-testid="complete-ride-btn" onClick={() => rideAction("complete")} className="flex-1 rounded-full font-bold">Mark completed</Button>
              <Button data-testid="cancel-ride-btn" onClick={() => rideAction("cancel")} variant="outline" className="flex-1 rounded-full font-bold text-destructive border-destructive/30">Cancel ride</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const StarRow = ({ label, value, onChange, prefix }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm font-semibold">{label}</span>
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`${prefix}-star-${n}`} onClick={() => onChange(n)}>
          <Star className={`w-5 h-5 transition-colors duration-150 ${n <= value ? "fill-[#FFB800] text-[#FFB800]" : "text-black/20"}`} />
        </button>
      ))}
    </div>
  </div>
);

function RateDialog({ rideId, person, onDone }) {
  const [open, setOpen] = useState(false);
  const [r, setR] = useState({ overall: 5, safety: 5, communication: 5, punctuality: 5, review: "" });

  const submit = async () => {
    try {
      await api.post(`/rides/${rideId}/rate`, { ratee_id: person.id, ...r });
      toast.success(`Thanks! You rated ${person.name}.`);
      setOpen(false);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm"><span className="font-bold">{person.name}</span> <span className="text-muted-foreground">· {person.role}</span></p>
      <Dialog open={open} onOpenChange={setOpen}>
        <Button data-testid={`rate-btn-${person.id}`} size="sm" onClick={() => setOpen(true)} className="rounded-full font-bold">
          <Star className="w-4 h-4 mr-1" /> Rate
        </Button>
        <DialogContent className="rounded-3xl">
          <DialogHeader><DialogTitle>Rate {person.name}</DialogTitle></DialogHeader>
          <StarRow label="Overall" value={r.overall} onChange={(v) => setR({ ...r, overall: v })} prefix="overall" />
          <StarRow label="Safety" value={r.safety} onChange={(v) => setR({ ...r, safety: v })} prefix="safety" />
          <StarRow label="Communication" value={r.communication} onChange={(v) => setR({ ...r, communication: v })} prefix="communication" />
          <StarRow label="Punctuality" value={r.punctuality} onChange={(v) => setR({ ...r, punctuality: v })} prefix="punctuality" />
          <Textarea data-testid="rating-review-input" placeholder="Optional review…" value={r.review}
            onChange={(e) => setR({ ...r, review: e.target.value })} className="rounded-2xl" />
          <Button data-testid="rating-submit-btn" onClick={submit} className="rounded-full font-bold">Submit rating</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
